import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="per-party-payment"
export default class extends Controller {
  static targets = [
    "modal",
    "overlay",
    "modalTitle",
    "modalDescription",
    "selectedPlanName",
    "selectedPrice",
    "selectedGuestLimit",
    "selectedFeatures",
    "paymentDetails",
    "paymentContainer",
    "loadingSpinner",
    "successMessage",
    "errorMessage",
    "errorText",
    "actionButtons",
    "confirmButton",
  ];

  static values = {
    partyRequestId: String,
    createUrl: String,
    confirmUrl: String,
    provider: String,
    currency: String,
  };

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.selectedPlan = null;
    this.purchaseId = null;
    this.stripe = null;
    this.elements = null;
  }

  disconnect() {
    this.removeKeydownListener();
  }

  selectPlan(event) {
    const card = event.currentTarget;
    const planType = card.dataset.planType;
    const planName = card.dataset.planName;
    const price = card.dataset.planPrice;
    const guestLimit = card.dataset.guestLimit;

    // Store selected plan data
    this.selectedPlan = {
      type: planType,
      name: planName,
      price: price,
      guestLimit: guestLimit,
    };

    // Extract features from the card
    const featuresHtml = card.querySelector("ul").innerHTML;

    // Update modal content
    this.modalTitleTarget.textContent = `Unlock ${planName}`;
    this.modalDescriptionTarget.textContent = `You're about to unlock premium features for this party with the ${planName} plan.`;
    this.selectedPlanNameTarget.textContent = planName;
    this.selectedPriceTarget.textContent = price;
    this.selectedGuestLimitTarget.textContent =
      guestLimit === "Unlimited"
        ? "Unlimited guests"
        : `Up to ${guestLimit} guests`;
    this.selectedFeaturesTarget.innerHTML = featuresHtml;

    // Reset UI state
    this.resetModalState();

    // Show modal
    this.modalTarget.classList.remove("hidden");
    this.addKeydownListener();
  }

  closeModal() {
    this.modalTarget.classList.add("hidden");
    this.selectedPlan = null;
    this.purchaseId = null;
    this.removeKeydownListener();
    this.resetModalState();
  }

  resetModalState() {
    // Show payment details, hide everything else
    if (this.hasPaymentDetailsTarget) {
      this.paymentDetailsTarget.classList.remove("hidden");
    }
    if (this.hasPaymentContainerTarget) {
      this.paymentContainerTarget.classList.add("hidden");
    }
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.add("hidden");
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden");
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden");
    }
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.remove("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false;
    }
  }

  async confirmPayment() {
    if (!this.selectedPlan) return;

    // Show loading state
    this.showLoading();

    try {
      // Create payment with currency from user's region
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({
          plan_type: this.selectedPlan.type,
          currency: this.currencyValue,
          provider: this.providerValue,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create payment");
      }

      // Store purchase ID
      this.purchaseId = data.purchase_id;

      // Handle payment provider response
      await this.handlePaymentResponse(data);
    } catch (error) {
      console.error("Payment creation error:", error);
      this.showError(
        error.message || "Failed to process payment. Please try again.",
      );
    }
  }

  async handlePaymentResponse(data) {
    const provider = data.provider;

    // Hide payment details and action buttons
    if (this.hasPaymentDetailsTarget) {
      this.paymentDetailsTarget.classList.add("hidden");
    }
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.add("hidden");
    }

    switch (provider) {
      case "stripe":
        await this.handleStripePayment(data.payment_details);
        break;
      case "paystack":
        await this.handlePaystackPayment(data.payment_details);
        break;
      default:
        throw new Error(`Unsupported payment provider: ${provider}`);
    }
  }

  async handleStripePayment(paymentDetails) {
    // Load Stripe.js if not already loaded
    if (!window.Stripe) {
      await this.loadStripe();
    }

    if (!paymentDetails.publishable_key) {
      this.showError("Stripe publishable key not configured");
      return;
    }

    if (!paymentDetails.client_secret) {
      this.showError("Payment client secret not available");
      return;
    }

    const stripe = window.Stripe(paymentDetails.publishable_key);

    // Create Stripe Elements
    const elements = stripe.elements({
      clientSecret: paymentDetails.client_secret,
    });

    // Create and mount the Payment Element
    const paymentElement = elements.create("payment");

    // Show payment container
    if (this.hasPaymentContainerTarget) {
      this.paymentContainerTarget.classList.remove("hidden");
    }
    this.hideLoading();

    // Mount to the payment-element div
    const container = this.element.querySelector("#payment-element");
    if (container) {
      paymentElement.mount("#payment-element");
    } else {
      this.showError("Payment container not found");
      return;
    }

    // Store stripe and elements for later use
    this.stripe = stripe;
    this.elements = elements;

    // Update the confirm button to submit the payment form
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.onclick = async (e) => {
        e.preventDefault();
        await this.submitStripePayment();
      };
    }
  }

  async submitStripePayment() {
    if (!this.stripe || !this.elements) {
      this.showError("Payment not initialized");
      return;
    }

    this.showLoading();

    const { error, paymentIntent } = await this.stripe.confirmPayment({
      elements: this.elements,
      redirect: "if_required",
    });

    if (error) {
      this.showError(error.message);
      return;
    }

    // Payment succeeded
    if (paymentIntent && paymentIntent.status === "succeeded") {
      // Confirm with backend
      await this.confirmWithBackend();
    }
  }

  async confirmWithBackend() {
    if (!this.purchaseId) {
      this.showError("Purchase ID not found");
      return;
    }

    try {
      const confirmUrl = this.confirmUrlValue.replace(":id", this.purchaseId);
      const response = await fetch(confirmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Payment confirmation failed");
      }

      // Show success message
      this.showSuccess();

      // Redirect to party overview after 2 seconds
      setTimeout(() => {
        window.location.href = `/parties/${this.partyRequestIdValue}`;
      }, 2000);
    } catch (error) {
      console.error("Payment confirmation error:", error);
      this.showError(error.message || "Failed to confirm payment");
    }
  }

  async handlePaystackPayment(paymentDetails) {
    // Paystack typically opens in a popup or redirects
    if (paymentDetails.authorization_url) {
      // For now, redirect to Paystack
      window.location.href = paymentDetails.authorization_url;
    } else if (paymentDetails.access_code) {
      // Alternative: use Paystack popup
      this.showError(
        "Paystack popup not yet implemented. Please contact support.",
      );
    }
  }


  async verifyPayment() {
    if (!this.purchaseId) return;

    try {
      const confirmUrl = this.confirmUrlValue.replace(":id", this.purchaseId);
      const response = await fetch(confirmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Payment verification failed");
      }

      if (data.success) {
        this.showSuccess();
      } else {
        throw new Error(data.error || "Payment not completed");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      this.showError(error.message || "Failed to verify payment.");
    }
  }

  showLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
      this.confirmButtonTarget.textContent = "Processing...";
    }
  }

  hideLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.add("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false;
      this.confirmButtonTarget.innerHTML = `
        <svg class="h-4 w-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Confirm & Pay
      `;
    }
  }

  showSuccess() {
    // Hide everything except success message
    if (this.hasPaymentDetailsTarget) {
      this.paymentDetailsTarget.classList.add("hidden");
    }
    if (this.hasPaymentContainerTarget) {
      this.paymentContainerTarget.classList.add("hidden");
    }
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.add("hidden");
    }
    this.hideLoading();

    // Show success message
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden");
    }

    // Redirect to party page after a delay
    setTimeout(() => {
      window.location.href = `/parties/${this.partyRequestIdValue}`;
    }, 2000);
  }

  showError(message) {
    // Hide loading
    this.hideLoading();

    // Show error message
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden");
    }

    // Hide success message if showing
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden");
    }

    // Show action buttons again
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.remove("hidden");
    }

    // Hide payment container
    if (this.hasPaymentContainerTarget) {
      this.paymentContainerTarget.classList.add("hidden");
    }
  }

  async loadStripe() {
    return new Promise((resolve, reject) => {
      if (window.Stripe) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  getCsrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""
    );
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }

  addKeydownListener() {
    document.addEventListener("keydown", this.handleKeydown);
  }

  removeKeydownListener() {
    document.removeEventListener("keydown", this.handleKeydown);
  }
}
